/**
 * Megamenu JavaScript - Simple & Readable
 * Desktop: Opens on hover OR focus, stays open while navigating inside
 * Mobile: Slide-in submenus with back buttons
 *
 * @package Felix
 */

import { debounce, isMobile, isDesktop } from '../shared/utils.js'

// Track if desktop has been initialized (DOM restructuring only happens once)
let desktopInitialized = false

export function initMegamenu() {
	// Always init mobile (handlers check viewport internally)
	initMobile()
	initSearchAllButtons()

	// Init desktop if we're on desktop viewport
	if (isDesktop()) {
		initDesktopIfNeeded()
	}

	// Handle resize: init desktop when crossing breakpoint
	const handleResize = debounce(() => {
		if (isDesktop()) {
			initDesktopIfNeeded()
		}
	}, 150)

	window.addEventListener('resize', handleResize)
}

/**
 * Initialize desktop functionality if not already done
 * DOM restructuring and event listeners only happen once
 */
function initDesktopIfNeeded() {
	if (desktopInitialized) return
	desktopInitialized = true

	restructureMegamenusForDesktop()
	initDesktop()
	initKeyboardNavigation()
}

/**
 * Move megamenus outside list items for proper backdrop-filter
 * Only runs on desktop
 */
function restructureMegamenusForDesktop() {
	const container = document.querySelector('.main-header__container')
	if (!container) return

	const megamenus = document.querySelectorAll('.main-header__nav .megamenu')

	megamenus.forEach((menu, index) => {
		const parentItem = menu.closest('.megamenu-l1__item--has-children')
		const parentLink = parentItem?.querySelector('.megamenu-l1__link')

		if (!parentItem || !parentLink) return

		const menuId = `megamenu-${index}`
		menu.dataset.megamenuId = menuId
		parentItem.dataset.megamenuId = menuId

		parentLink.setAttribute('aria-controls', menuId)
		menu.setAttribute('id', menuId)

		container.appendChild(menu)
	})
}

/**
 * Set container height to accommodate megamenu
 */
function setContainerHeight(megamenu) {
	// Only set height on desktop
	if (isMobile()) return

	const container = document.querySelector('.main-header__container')
	if (!container) return

	requestAnimationFrame(() => {
		const megamenuHeight = megamenu.offsetHeight
		const headerBaseHeight = 60
		const totalHeight = headerBaseHeight + megamenuHeight

		container.style.height = `${totalHeight}px`
	})
}

/**
 * Reset container height to default
 */
function resetContainerHeight() {
	const container = document.querySelector('.main-header__container')
	if (!container) return

	container.style.height = ''
}

/**
 * Desktop: Hover and Focus
 */
function initDesktop() {
	const items = document.querySelectorAll('.megamenu-l1__item--has-children')

	// Store close timeouts per menu to prevent race conditions
	const closeTimeouts = new Map()
	const CLOSE_DELAY = 100 // ms delay before closing

	items.forEach((item) => {
		const link = item.querySelector('.megamenu-l1__link')
		const menuId = item.dataset.megamenuId
		const megamenu = document.querySelector(`.megamenu[data-megamenu-id="${menuId}"]`)

		if (!link || !megamenu) return

		// Helper to open this menu
		const openMenu = () => {
			// Cancel any pending close for this menu
			if (closeTimeouts.has(menuId)) {
				clearTimeout(closeTimeouts.get(menuId))
				closeTimeouts.delete(menuId)
			}

			// Close any other open menus immediately
			items.forEach((otherItem) => {
				const otherId = otherItem.dataset.megamenuId
				if (otherId !== menuId) {
					const otherLink = otherItem.querySelector('.megamenu-l1__link')
					const otherMenu = document.querySelector(`.megamenu[data-megamenu-id="${otherId}"]`)
					if (otherLink && otherMenu) {
						// Cancel pending close for other menu
						if (closeTimeouts.has(otherId)) {
							clearTimeout(closeTimeouts.get(otherId))
							closeTimeouts.delete(otherId)
						}
						otherLink.setAttribute('aria-expanded', 'false')
						otherMenu.classList.remove('is-open')
					}
				}
			})

			link.setAttribute('aria-expanded', 'true')
			megamenu.classList.add('is-open')
			setContainerHeight(megamenu)
			document.dispatchEvent(new CustomEvent('quick-search:close'))
		}

		// Helper to close this menu (with delay)
		const closeMenu = () => {
			// Set a delayed close
			const timeoutId = setTimeout(() => {
				link.setAttribute('aria-expanded', 'false')
				megamenu.classList.remove('is-open')
				resetContainerHeight()
				closeTimeouts.delete(menuId)
			}, CLOSE_DELAY)
			closeTimeouts.set(menuId, timeoutId)
		}

		// Helper to cancel close
		const cancelClose = () => {
			if (closeTimeouts.has(menuId)) {
				clearTimeout(closeTimeouts.get(menuId))
				closeTimeouts.delete(menuId)
			}
		}

		item.addEventListener('mouseenter', openMenu)
		item.addEventListener('mouseleave', closeMenu)

		megamenu.addEventListener('mouseenter', () => {
			cancelClose()
			link.setAttribute('aria-expanded', 'true')
			megamenu.classList.add('is-open')
			setContainerHeight(megamenu)
		})

		megamenu.addEventListener('mouseleave', closeMenu)

		item.addEventListener('focusin', () => {
			link.setAttribute('aria-expanded', 'true')
			megamenu.classList.add('is-open')
			setContainerHeight(megamenu)
		})

		item.addEventListener('focusout', (e) => {
			if (!item.contains(e.relatedTarget) && !megamenu.contains(e.relatedTarget)) {
				link.setAttribute('aria-expanded', 'false')
				megamenu.classList.remove('is-open')
				resetContainerHeight()
			}
		})

		megamenu.addEventListener('focusin', () => {
			link.setAttribute('aria-expanded', 'true')
			megamenu.classList.add('is-open')
			setContainerHeight(megamenu)
		})

		megamenu.addEventListener('focusout', (e) => {
			if (!item.contains(e.relatedTarget) && !megamenu.contains(e.relatedTarget)) {
				link.setAttribute('aria-expanded', 'false')
				megamenu.classList.remove('is-open')
				resetContainerHeight()
			}
		})

		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && (item.contains(document.activeElement) || megamenu.contains(document.activeElement))) {
				link.setAttribute('aria-expanded', 'false')
				megamenu.classList.remove('is-open')
				resetContainerHeight()
				link.focus()
			}
		})
	})

	// Handle window resize - recalculate height if megamenu is open
	let resizeTimeout
	window.addEventListener('resize', () => {
		clearTimeout(resizeTimeout)
		resizeTimeout = setTimeout(() => {
			const openMegamenu = document.querySelector('.megamenu.is-open')
			if (openMegamenu && isDesktop()) {
				setContainerHeight(openMegamenu)
			} else if (isMobile()) {
				// Clear inline height on mobile
				resetContainerHeight()
			}
		}, 150)
	})
}

/**
 * Mobile: Slide-in Submenus
 */
function initMobile() {
	const level1Items = document.querySelectorAll('.main-header__menu .megamenu-l1__item--has-children')

	level1Items.forEach((item) => {
		const link = item.querySelector('.megamenu-l1__link')
		const megamenu = item.querySelector('.megamenu')

		if (!link || !megamenu) return

		// Level 1 click → open Level 2
		link.addEventListener('click', (e) => {
			if (isMobile()) {
				e.preventDefault()
				showLevel2(megamenu)
			}
		})

		// Level 2 back button (server-rendered)
		const level2BackBtn = megamenu.querySelector('.megamenu__back--level-2')
		if (level2BackBtn) {
			level2BackBtn.addEventListener('click', () => {
				hideLevel2(megamenu)
			})
		}
	})

	// Level 2 triggers → open Level 3
	const level2Triggers = document.querySelectorAll('.megamenu-l2__trigger')

	level2Triggers.forEach((trigger) => {
		const submenu = trigger.parentElement.querySelector('.megamenu-l3__list')
		if (!submenu) return

		trigger.addEventListener('click', () => {
			if (isMobile()) {
				// Reset parent megamenu scroll so Level 3 starts at top
				const megamenu = trigger.closest('.megamenu')
				if (megamenu) {
					megamenu.scrollTop = 0
				}
				showLevel3(submenu)
			}
		})

		// Level 3 back button (server-rendered)
		const level3BackBtn = submenu.querySelector('.megamenu__back--level-3')
		if (level3BackBtn) {
			level3BackBtn.addEventListener('click', () => {
				hideLevel3(submenu)
			})
		}
	})
}

/**
 * Show Level 2 view (megamenu)
 */
function showLevel2(megamenu) {
	const parentItem = megamenu.closest('.megamenu-l1__item')
	const parentLink = parentItem?.querySelector('.megamenu-l1__link')
	if (parentLink) {
		parentLink.setAttribute('aria-expanded', 'true')
	}

	const mainMenuList = megamenu.closest('.main-header__mobile-menu')
	if (mainMenuList) {
		const allLevel1Items = mainMenuList.querySelectorAll(':scope > .megamenu-l1__item')
		allLevel1Items.forEach((item) => {
			if (!item.contains(megamenu)) {
				item.style.display = 'none'
			}
		})
	}

	megamenu.classList.add('is-active')
}

/**
 * Hide Level 2 view (megamenu)
 */
function hideLevel2(megamenu) {
	const parentItem = megamenu.closest('.megamenu-l1__item')
	const parentLink = parentItem?.querySelector('.megamenu-l1__link')
	if (parentLink) {
		parentLink.setAttribute('aria-expanded', 'false')
	}

	megamenu.classList.remove('is-active')

	const mainMenuList = megamenu.closest('.main-header__mobile-menu')
	if (mainMenuList) {
		const allLevel1Items = mainMenuList.querySelectorAll(':scope > .megamenu-l1__item')
		allLevel1Items.forEach((item) => {
			item.style.display = ''
		})
	}

	const openSubmenus = megamenu.querySelectorAll('.megamenu-l3__list.is-active')
	openSubmenus.forEach((submenu) => submenu.classList.remove('is-active'))
}

/**
 * Show Level 3 view (submenu)
 */
function showLevel3(submenu) {
	submenu.classList.add('is-active')
	submenu.scrollTop = 0
}

/**
 * Hide Level 3 view (submenu)
 */
function hideLevel3(submenu) {
	submenu.classList.remove('is-active')
}

/**
 * The first header control that visually follows the navigation.
 *
 * Excludes tabindex="-1": the search panel's own results live in here and are
 * deliberately out of the tab order, so the first real stop is the search input.
 *
 * @returns {HTMLElement|null}
 */
function headerControlAfterNav() {
	const actions = document.querySelector('.main-header__desktop-actions')

	if (!actions) return null

	const selector = 'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"])'

	return Array.from(actions.querySelectorAll(selector)).find((el) => el.getClientRects().length > 0) || null
}

function initKeyboardNavigation() {
	const items = document.querySelectorAll('.megamenu-l1__item--has-children')
	const allL1Items = document.querySelectorAll('.megamenu-l1__item')

	items.forEach((item, index) => {
		const link = item.querySelector('.megamenu-l1__link')
		const menuId = item.dataset.megamenuId
		const megamenu = document.querySelector(`.megamenu[data-megamenu-id="${menuId}"]`)

		if (!link || !megamenu) return

		const getFocusableElements = () => {
			const elements = megamenu.querySelectorAll('a[href], button:not([disabled])')
			return Array.from(elements).filter((el) => {
				// getClientRects() is empty whenever the element OR ANY ANCESTOR is
				// unrendered; the element's own computed style is not enough. The
				// mobile back button is display:flex inside a lg:hidden header, so it
				// passed the old test, sat first in this list, and swallowed every Tab
				// into the desktop menu: focus() on an unrendered element does nothing,
				// and the handler had already called preventDefault().
				if (el.getClientRects().length === 0) return false
				return window.getComputedStyle(el).visibility !== 'hidden'
			})
		}

		link.addEventListener('keydown', (e) => {
			if (e.key === 'Tab' && !e.shiftKey && link.getAttribute('aria-expanded') === 'true') {
				const focusable = getFocusableElements()
				if (focusable.length > 0) {
					e.preventDefault()
					focusable[0].focus()
				}
			}
		})

		megamenu.addEventListener('keydown', (e) => {
			const focusable = Array.from(getFocusableElements())
			const firstElement = focusable[0]
			const lastElement = focusable[focusable.length - 1]

			if (e.key === 'Tab' && e.shiftKey && document.activeElement === firstElement) {
				e.preventDefault()
				link.focus()
			}

			if (e.key === 'Tab' && !e.shiftKey && document.activeElement === lastElement) {
				const currentIndex = Array.from(allL1Items).indexOf(item)
				const nextL1Item = allL1Items[currentIndex + 1]

				// The panels are appended to the end of the header container by
				// restructureMegamenusForDesktop(), so they sit AFTER the desktop
				// actions in the DOM even though they read as part of the nav. Native
				// Tab out of the last panel therefore skips the rest of the header --
				// search and the account link -- and lands in the page body. So the
				// last menu hands focus on explicitly, the same way the others hand it
				// to the next top-level item.
				const nextTarget = nextL1Item
					? nextL1Item.querySelector('.megamenu-l1__link')
					: headerControlAfterNav()

				if (!nextTarget) {
					return
				}

				e.preventDefault()
				link.setAttribute('aria-expanded', 'false')
				megamenu.classList.remove('is-open')
				resetContainerHeight()
				requestAnimationFrame(() => {
					nextTarget.focus()
				})
			}
		})
	})
}

function initSearchAllButtons() {
	const searchButtons = document.querySelectorAll('[data-trigger-search]')

	searchButtons.forEach((button) => {
		button.addEventListener('click', (e) => {
			e.preventDefault()

			const openMenus = document.querySelectorAll('.megamenu.is-active')
			openMenus.forEach((menu) => {
				menu.classList.remove('is-active')
				menu.classList.add('is-hidden')
			})

			const expandedLinks = document.querySelectorAll('.megamenu-l1__link[aria-expanded="true"]')
			expandedLinks.forEach((link) => {
				link.setAttribute('aria-expanded', 'false')
			})

			document.dispatchEvent(new CustomEvent('quick-search:open'))
		})
	})
}
